import { useState, useEffect } from "react";
import { Sparkles, MapPin, BedDouble, Bath, Maximize, Compass, Loader2, Building2, ShoppingCart, GraduationCap, Heart, Car, Laptop, ChevronLeft, ChevronRight, CheckCircle, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "918142445677";

const FeaturedListings = () => {
  const [listings, setListings] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [location, setLocation] = useState("Kokapet");
  const [bedrooms, setBedrooms] = useState("2");

  useEffect(() => {
    axios.get(`${API}/featured-listings`).then(r => setListings(r.data)).catch(() => {});
  }, []);

  const generateListing = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/featured-listing`, { location, bedrooms: parseInt(bedrooms), property_type: "rent" });
      if (!res.data.error) {
        setListings(prev => [res.data, ...prev]);
        setSelectedListing(res.data);
      }
    } catch { }
    setGenerating(false);
  };

  const getWA = (l) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi, I'm interested in "${l.title}" in ${l.location} for ${l.price} listed on Junction.`)}`;

  // Detail View
  if (selectedListing) {
    const l = selectedListing;
    const images = l.images || {};
    return (
      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden" data-testid="featured-detail">
        <button onClick={() => setSelectedListing(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-indigo px-6 pt-4" data-testid="featured-back">
          <ChevronLeft size={16} />Back to listings
        </button>

        {/* Hero Image */}
        <div className="relative h-64 sm:h-80 mx-6 mt-3 rounded-2xl overflow-hidden">
          <img src={images.hero || images.living_room} alt={l.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          <div className="absolute bottom-4 left-4 right-4">
            <Badge className="bg-marigold text-indigo rounded-full text-xs mb-2">Featured</Badge>
            <h2 className="font-heading text-2xl font-semibold text-white">{l.title}</h2>
            <div className="flex items-center gap-2 mt-1 text-white/80 text-sm"><MapPin size={14} />{l.location}</div>
          </div>
        </div>

        <div className="p-6">
          {/* Tagline */}
          {l.tagline && <p className="text-sm text-terracotta font-medium mb-4 italic">"{l.tagline}"</p>}

          {/* Price + Key Details */}
          <div className="flex flex-wrap gap-6 mb-6 pb-6 border-b border-gray-100">
            <div><p className="text-xs text-gray-400">Price</p><p className="font-heading text-2xl font-semibold text-indigo">{l.price}</p></div>
            {l.bedrooms && <div className="flex items-center gap-1.5"><BedDouble size={16} className="text-gray-400" /><span className="text-sm text-gray-700">{l.bedrooms} Bed</span></div>}
            {l.bathrooms && <div className="flex items-center gap-1.5"><Bath size={16} className="text-gray-400" /><span className="text-sm text-gray-700">{l.bathrooms} Bath</span></div>}
            {l.area_sqft && <div className="flex items-center gap-1.5"><Maximize size={16} className="text-gray-400" /><span className="text-sm text-gray-700">{l.area_sqft} sqft</span></div>}
            {l.facing && <div className="flex items-center gap-1.5"><Compass size={16} className="text-gray-400" /><span className="text-sm text-gray-700">{l.facing} facing</span></div>}
            {l.floor && <div className="text-sm text-gray-700">Floor: {l.floor}</div>}
            {l.furnishing && <Badge variant="outline" className="rounded-full text-xs">{l.furnishing}</Badge>}
          </div>

          {/* Description */}
          {l.description && <p className="text-sm text-gray-600 leading-relaxed mb-6">{l.description}</p>}

          {/* Image Gallery */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {Object.entries(images).map(([key, url]) => url && (
              <div key={key} className="rounded-xl overflow-hidden h-24">
                <img src={url} alt={key} className="w-full h-full object-cover hover:scale-105 transition-transform" />
              </div>
            ))}
          </div>

          {/* Amenities */}
          {l.amenities && l.amenities.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading text-sm font-semibold text-indigo mb-3 flex items-center gap-2"><CheckCircle size={14} className="text-green-500" />Amenities</h3>
              <div className="flex flex-wrap gap-2">
                {l.amenities.map((a, i) => <Badge key={i} variant="secondary" className="rounded-full text-xs bg-green-50 text-green-700 font-normal">{a}</Badge>)}
              </div>
            </div>
          )}

          {/* Community */}
          {l.community_highlights && l.community_highlights.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading text-sm font-semibold text-indigo mb-3 flex items-center gap-2"><Building2 size={14} className="text-indigo" />Community Highlights</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {l.community_highlights.map((c, i) => <div key={i} className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2"><CheckCircle size={12} className="text-marigold shrink-0 mt-0.5" />{c}</div>)}
              </div>
            </div>
          )}

          {/* Nearby Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {l.nearby_shopping && l.nearby_shopping.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4">
                <h4 className="font-heading text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5"><ShoppingCart size={13} />Shopping</h4>
                {l.nearby_shopping.map((s, i) => <p key={i} className="text-[11px] text-amber-700 mb-1">{s}</p>)}
              </div>
            )}
            {l.nearby_schools && l.nearby_schools.length > 0 && (
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-heading text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1.5"><GraduationCap size={13} />Schools</h4>
                {l.nearby_schools.map((s, i) => <p key={i} className="text-[11px] text-blue-700 mb-1">{s}</p>)}
              </div>
            )}
            {l.nearby_hospitals && l.nearby_hospitals.length > 0 && (
              <div className="bg-rose-50 rounded-xl p-4">
                <h4 className="font-heading text-xs font-semibold text-rose-800 mb-2 flex items-center gap-1.5"><Heart size={13} />Hospitals</h4>
                {l.nearby_hospitals.map((s, i) => <p key={i} className="text-[11px] text-rose-700 mb-1">{s}</p>)}
              </div>
            )}
            {l.road_connectivity && l.road_connectivity.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-heading text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1.5"><Car size={13} />Road Connectivity</h4>
                {l.road_connectivity.map((s, i) => <p key={i} className="text-[11px] text-gray-700 mb-1">{s}</p>)}
              </div>
            )}
            {l.it_hubs_nearby && l.it_hubs_nearby.length > 0 && (
              <div className="bg-indigo/5 rounded-xl p-4">
                <h4 className="font-heading text-xs font-semibold text-indigo mb-2 flex items-center gap-1.5"><Laptop size={13} />IT Hubs</h4>
                {l.it_hubs_nearby.map((s, i) => <p key={i} className="text-[11px] text-indigo/80 mb-1">{s}</p>)}
              </div>
            )}
          </div>

          {/* Why This Location */}
          {l.why_this_location && (
            <div className="bg-marigold/5 rounded-xl p-4 mb-6 border border-marigold/10">
              <h4 className="font-heading text-xs font-semibold text-indigo mb-1 flex items-center gap-1.5"><MapPin size={13} className="text-marigold" />Why {l.location}?</h4>
              <p className="text-xs text-gray-600 leading-relaxed">{l.why_this_location}</p>
            </div>
          )}

          {/* CTA */}
          <div className="flex gap-3">
            <a href={`tel:+91${WHATSAPP.slice(2)}`} className="flex-1">
              <Button variant="outline" className="w-full rounded-xl text-sm border-indigo text-indigo hover:bg-indigo hover:text-white" data-testid="featured-call"><Phone size={14} className="mr-2" />Call Now</Button>
            </a>
            <a href={getWA(l)} target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button className="w-full rounded-xl text-sm bg-green-600 hover:bg-green-700 text-white" data-testid="featured-whatsapp"><MessageCircle size={14} className="mr-2" />WhatsApp</Button>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div data-testid="featured-listings">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-heading text-lg font-semibold text-indigo flex items-center gap-2"><Sparkles size={16} className="text-marigold" />Featured Listings</h3>
          <p className="text-xs text-gray-400">AI-generated premium listings with full neighborhood details</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="rounded-xl h-8 text-xs w-32 border-gray-200"><SelectValue /></SelectTrigger>
            <SelectContent>{["Kokapet","Gandipet","Narsingi","Gachibowli","Kondapur","Madhapur","Kukatpally"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={bedrooms} onValueChange={setBedrooms}>
            <SelectTrigger className="rounded-xl h-8 text-xs w-24 border-gray-200"><SelectValue /></SelectTrigger>
            <SelectContent>{["1","2","3","4"].map(b => <SelectItem key={b} value={b}>{b} BHK</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={generateListing} disabled={generating} className="bg-marigold text-indigo hover:bg-marigold/90 rounded-full h-8 text-xs px-4" data-testid="generate-featured">
            {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
            {generating ? "Creating..." : "Generate"}
          </Button>
        </div>
      </div>

      {listings.length === 0 && !generating && (
        <div className="text-center py-10 bg-gray-50 rounded-2xl border border-gray-100">
          <Sparkles size={24} className="text-marigold mx-auto mb-3" />
          <p className="text-sm text-gray-500 mb-3">No featured listings yet. Click "Generate" to create one!</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {listings.map((l, i) => (
          <button key={i} onClick={() => setSelectedListing(l)} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all text-left group" data-testid={`featured-card-${i}`}>
            <div className="h-40 overflow-hidden relative">
              <img src={l.images?.hero || l.images?.living_room || ""} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <Badge className="absolute top-3 left-3 bg-marigold text-indigo rounded-full text-[10px]">Featured</Badge>
            </div>
            <div className="p-4">
              <h4 className="font-heading font-medium text-indigo text-sm mb-1 group-hover:text-terracotta transition-colors">{l.title}</h4>
              <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-2"><MapPin size={10} />{l.location}</div>
              <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-2">
                {l.bedrooms && <span>{l.bedrooms} BHK</span>}
                {l.area_sqft && <span>{l.area_sqft} sqft</span>}
                {l.furnishing && <span>{l.furnishing}</span>}
              </div>
              <div className="flex items-center justify-between">
                <span className="font-heading font-semibold text-indigo text-base">{l.price}</span>
                <span className="text-[10px] text-marigold font-medium">View Details</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default FeaturedListings;
